/**
 * FollowupMessagesList — renders the ordered list of messages with add/move/edit/delete
 * affordances (Story 7.8).
 *
 * Reorder uses move-up/move-down buttons (dnd-kit not installed in the admin
 * workspace — documented in Dev Agent Record). Each per-card interaction
 * dispatches a mutation to the parent which handles optimistic + invalidation.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MessageCard, type MessageCardDto } from '@/components/followup/MessageCard';

interface Props {
  messages: MessageCardDto[];
  submitting?: boolean;
  onCreate: (content: string) => void;
  onUpdateContent: (id: string, content: string) => void;
  onDelete: (id: string) => void;
  onReorder: (id: string, newPosition: number) => void;
}

export function FollowupMessagesList({
  messages,
  submitting,
  onCreate,
  onUpdateContent,
  onDelete,
  onReorder,
}: Props) {
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState('');

  function handleAdd() {
    if (draft.trim() === '') return;
    onCreate(draft);
    setDraft('');
    setAddOpen(false);
  }

  function handleMoveUp(id: string) {
    const idx = messages.findIndex((m) => m.id === id);
    if (idx <= 0) return;
    onReorder(id, idx); // 1-based position; idx is 0-based new target.
  }

  function handleMoveDown(id: string) {
    const idx = messages.findIndex((m) => m.id === id);
    if (idx < 0 || idx >= messages.length - 1) return;
    onReorder(id, idx + 2); // move down by one → idx+2 (1-based).
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Pool de mensagens</h2>
          <p className="text-xs text-muted-foreground">
            A ordem controla a rotação de variantes (anti-repetição).
          </p>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)} disabled={submitting}>
          <Plus size={16} aria-hidden className="mr-1" />
          Adicionar mensagem
        </Button>
      </div>

      {messages.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          Nenhuma mensagem no pool. Adicione a primeira para começar.
        </div>
      ) : (
        <div className="space-y-2">
          {messages.map((m, i) => (
            <MessageCard
              key={m.id}
              message={m}
              isFirst={i === 0}
              isLast={i === messages.length - 1}
              submitting={submitting}
              onSaveContent={onUpdateContent}
              onDelete={onDelete}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
            />
          ))}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova mensagem de followup</DialogTitle>
            <DialogDescription>
              Será adicionada ao final do pool. Você pode reordenar depois.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <textarea
              className="flex min-h-[110px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={draft}
              disabled={submitting}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ex.: Oi {{1}}, ainda posso te ajudar com o upgrade?"
              aria-label="Conteúdo da mensagem"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDraft('');
                  setAddOpen(false);
                }}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button onClick={handleAdd} disabled={submitting || draft.trim() === ''}>
                Adicionar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FollowupMessagesList;
